import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Video, GripVertical, Scissors, ChevronLeft, ChevronRight } from "lucide-react";
import "./VideoTimeline.css";

/**
 * VideoTimeline - Timeline component for video montage
 *
 * Features:
 * - Visual representation of video segments
 * - Drag & drop to reorder segments
 * - Transition zones between segments
 * - Playhead with seek functionality
 */

export default function VideoTimeline({
  segments = [],
  currentTimeMs = 0,
  timelineData = null,
  onSeek,
  onSegmentReorder,
  onSegmentClick,
  onTransitionClick,
  onAddSegment,
  activeSegmentId = null
}) {
  const MIN_SEGMENT_WIDTH_PX = 16;
  const [draggedSegmentIndex, setDraggedSegmentIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [trackViewportWidthPx, setTrackViewportWidthPx] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const timelineRef = useRef(null);
  const trackViewportRef = useRef(null);
  const isDraggingPlayhead = useRef(false);

  // Calculate total duration
  const totalDurationMs = segments.reduce((sum, seg) => sum + (seg.duration || 0) * 1000, 0);

  const trackLayout = useMemo(() => {
    if (segments.length === 0) {
      return { widthPx: Math.max(trackViewportWidthPx, 0), positions: [] };
    }

    const fallbackViewport = trackViewportWidthPx > 0 ? trackViewportWidthPx : segments.length * MIN_SEGMENT_WIDTH_PX;
    const pxPerMs = totalDurationMs > 0 ? fallbackViewport / totalDurationMs : 0;
    let cumulativeTime = 0;
    let cumulativePx = 0;

    const positions = segments.map((seg) => {
      const startMs = cumulativeTime;
      const durationMs = Math.max(0, (seg.duration || 0) * 1000);
      const widthPx = Math.max(MIN_SEGMENT_WIDTH_PX, durationMs * pxPerMs);
      const startPx = cumulativePx;
      cumulativeTime += durationMs;
      cumulativePx += widthPx;
      return { startPx, widthPx, startMs, durationMs };
    });

    return { widthPx: Math.max(fallbackViewport, cumulativePx), positions };
  }, [segments, totalDurationMs, trackViewportWidthPx]);

  const updateScrollButtons = useCallback(() => {
    const viewport = trackViewportRef.current;
    if (!viewport) return;
    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    setCanScrollLeft(viewport.scrollLeft > 1);
    setCanScrollRight(viewport.scrollLeft < maxScrollLeft - 1);
  }, []);

  useEffect(() => {
    const updateTrackWidth = () => {
      if (!trackViewportRef.current) return;
      setTrackViewportWidthPx(trackViewportRef.current.clientWidth);
      updateScrollButtons();
    };

    updateTrackWidth();
    window.addEventListener("resize", updateTrackWidth);
    return () => {
      window.removeEventListener("resize", updateTrackWidth);
    };
  }, [updateScrollButtons]);

  useEffect(() => {
    updateScrollButtons();
  }, [trackLayout.widthPx, updateScrollButtons]);

  // ── Playhead seek helpers ──
  const seekFromEvent = useCallback(
    (clientX) => {
      if (!timelineRef.current || !onSeek || totalDurationMs <= 0) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      onSeek((x / rect.width) * totalDurationMs);
    },
    [onSeek, totalDurationMs]
  );

  // Ruler mousedown → start drag
  const handleRulerMouseDown = useCallback(
    (e) => {
      if (e.button !== 0) return; // left-click only
      isDraggingPlayhead.current = true;
      seekFromEvent(e.clientX);
    },
    [seekFromEvent]
  );

  // Window mousemove / mouseup for playhead drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingPlayhead.current) return;
      seekFromEvent(e.clientX);
    };
    const handleMouseUp = () => {
      isDraggingPlayhead.current = false;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [seekFromEvent]);

  // ── Double-click on track → insert segment ──
  const handleTrackDoubleClick = useCallback(
    (e) => {
      if (!onAddSegment) return;
      // Only trigger on the track background, not on segment children
      if (e.target.closest(".video-timeline-segment")) return;
      onAddSegment();
    },
    [onAddSegment]
  );

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget);
    setDraggedSegmentIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();

    if (draggedSegmentIndex === null || draggedSegmentIndex === dropIndex) {
      setDraggedSegmentIndex(null);
      setDragOverIndex(null);
      return;
    }

    if (onSegmentReorder) {
      onSegmentReorder(draggedSegmentIndex, dropIndex);
    }

    setDraggedSegmentIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedSegmentIndex(null);
    setDragOverIndex(null);
  };

  const segmentPositions = trackLayout.positions;

  const scrollSegments = useCallback((direction) => {
    const viewport = trackViewportRef.current;
    if (!viewport) return;
    const delta = Math.max(120, viewport.clientWidth * 0.6);
    viewport.scrollBy({ left: delta * direction, behavior: "smooth" });
  }, []);

  // Calculate playhead position
  const playheadPct = totalDurationMs > 0 ? (currentTimeMs / totalDurationMs) * 100 : 0;

  // Generate time markers (every second)
  const timeMarkers = [];
  const markerIntervalMs = 1000; // 1 second
  for (let time = 0; time <= totalDurationMs; time += markerIntervalMs) {
    timeMarkers.push({
      time,
      position: totalDurationMs > 0 ? (time / totalDurationMs) * 100 : 0,
      label: `${Math.floor(time / 1000)}s`
    });
  }

  return (
    <div className="video-timeline-container">
      {/* Ruler */}
      <div className="video-timeline-ruler">
        <div className="video-timeline-ruler-label">Timeline</div>

        <div
          ref={timelineRef}
          className={`video-timeline-ruler-track${isDraggingPlayhead.current ? " scrubbing" : ""}`}
          onMouseDown={handleRulerMouseDown}
        >
          {/* Time markers */}
          {timeMarkers.map((marker, i) => (
            <div
              key={i}
              className="video-timeline-marker"
              style={{ left: `${marker.position}%` }}
            >
              <div className="video-timeline-marker-line" />
              <div className="video-timeline-marker-label">{marker.label}</div>
            </div>
          ))}

          {/* Playhead */}
          <div
            className="video-timeline-playhead"
            style={{ left: `${playheadPct}%` }}
          >
            <div className="video-timeline-playhead-head" />
            <div className="video-timeline-playhead-line" />
          </div>
        </div>
      </div>

      {/* Segments Track */}
      <div className="video-timeline-track-container">
        <div className="video-timeline-track-label">
          <Video size={16} className="video-timeline-track-icon" />
          <span>Segments ({segments.length})</span>
        </div>

        <div className="video-timeline-track-shell">
          <button
            type="button"
            className="video-timeline-scroll-btn"
            onClick={() => scrollSegments(-1)}
            disabled={!canScrollLeft}
            aria-label="Voir les segments precedents"
          >
            <ChevronLeft size={16} />
          </button>

          <div
            ref={trackViewportRef}
            className="video-timeline-track-scroll"
            onScroll={updateScrollButtons}
          >
            <div
              className="video-timeline-track"
              style={{ width: `${trackLayout.widthPx}px` }}
              onDoubleClick={handleTrackDoubleClick}
              title={onAddSegment ? "Double-cliquez pour ajouter un segment" : undefined}
            >
              {segments.length === 0 ? (
                <div className="video-timeline-empty">
                  <p>Aucun segment — double-cliquez pour en ajouter</p>
                </div>
              ) : (
                <>
                  {segments.map((segment, index) => {
                    const pos = segmentPositions[index];
                    const isActive = activeSegmentId === segment.segment_id;
                    const isDragging = draggedSegmentIndex === index;
                    const isDragOver = dragOverIndex === index;
                    const isPlaying = currentTimeMs >= pos.startMs && currentTimeMs < pos.startMs + pos.durationMs;

                    // Get prompt text
                    const promptText = typeof segment.prompt === "string"
                      ? segment.prompt
                      : segment.prompt?.text || "Sans titre";

                    // Video status
                    const videoStatus = segment.video?.status || "pending";
                    const hasVideo = videoStatus === "completed" && segment.video?.url;

                    // Image statuses for micro-cards
                    const startImgStatus = segment.start_image?.status || "pending";
                    const endImgStatus = segment.end_image?.status || "pending";
                    const startImgUrl = segment.start_image?.url;

                    return (
                      <React.Fragment key={segment.segment_id || index}>
                        {/* Transition zone */}
                        {index > 0 && (
                          <div
                            className="video-timeline-transition-zone"
                            style={{
                              left: `${segmentPositions[index - 1].startPx + segmentPositions[index - 1].widthPx}px`,
                              width: "8px"
                            }}
                            onClick={() => onTransitionClick && onTransitionClick(index - 1, index)}
                            title={`Transition: ${segments[index - 1].transition_out?.type || "none"} \u2192 ${segment.transition_in?.type || "none"}`}
                          >
                            <div className="video-timeline-transition-indicator">
                              <Scissors size={10} />
                            </div>
                          </div>
                        )}

                        {/* Segment with micro-card */}
                        <div
                          className={`video-timeline-segment
                            ${isActive ? "active" : ""}
                            ${isDragging ? "dragging" : ""}
                            ${isDragOver ? "drag-over" : ""}
                            ${isPlaying ? "playing" : ""}
                            ${hasVideo ? "has-video" : "no-video"}
                          `}
                          style={{
                            left: `${pos.startPx}px`,
                            width: `${pos.widthPx}px`
                          }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          onClick={() => onSegmentClick && onSegmentClick(segment, index)}
                        >
                          {/* Frame strip thumbnails (or fallback to single start_image) */}
                          {(() => {
                            const tlSeg = timelineData?.segments?.find(s => s.segment_id === segment.segment_id);
                            const frames = tlSeg?.frames || segment.timeline_data?.frames;
                            if (frames && frames.length > 0) {
                              return (
                                <div className="video-timeline-frame-strip">
                                  {frames.map((url, fi) => (
                                    <img
                                      key={fi}
                                      src={url}
                                      alt=""
                                      className="video-timeline-frame-strip-img"
                                      draggable={false}
                                    />
                                  ))}
                                </div>
                              );
                            }
                            if (startImgUrl) {
                              return (
                                <img
                                  src={startImgUrl}
                                  alt=""
                                  className="video-timeline-segment-thumb"
                                  draggable={false}
                                />
                              );
                            }
                            return null;
                          })()}

                          {/* Waveform SVG */}
                          {(() => {
                            const tlSeg = timelineData?.segments?.find(s => s.segment_id === segment.segment_id);
                            const waveform = tlSeg?.waveform || segment.timeline_data?.waveform;
                            if (!waveform || waveform.length === 0) return null;
                            const barCount = waveform.length;
                            const barW = 100 / barCount;
                            return (
                              <svg
                                className="video-timeline-waveform"
                                viewBox={`0 0 100 20`}
                                preserveAspectRatio="none"
                              >
                                {waveform.map((v, wi) => (
                                  <rect
                                    key={wi}
                                    x={wi * barW}
                                    y={20 - v * 18}
                                    width={barW * 0.8}
                                    height={v * 18}
                                    rx={0.3}
                                    fill="rgba(0, 201, 255, 0.55)"
                                  />
                                ))}
                              </svg>
                            );
                          })()}

                          <div className="video-timeline-segment-content">
                            <div className="video-timeline-segment-header">
                              <GripVertical size={12} className="video-timeline-segment-grip" />
                              <span className="video-timeline-segment-number">{index + 1}</span>
                            </div>

                            <div className="video-timeline-segment-body">
                              <span className="video-timeline-segment-title">
                                {promptText.substring(0, 30)}
                                {promptText.length > 30 ? "..." : ""}
                              </span>
                              <span className="video-timeline-segment-duration">
                                {segment.duration}s
                              </span>
                            </div>

                            {/* Micro-card: status indicators row */}
                            <div className="video-timeline-segment-statuses">
                              <span className={`tl-micro-badge tl-micro-badge-${startImgStatus}`} title="Image de debut">
                                {startImgStatus === "completed" ? "\u2713" : startImgStatus === "generating" ? "\u22ef" : "\u25cb"}
                              </span>
                              <span className={`tl-micro-badge tl-micro-badge-${endImgStatus}`} title="Image de fin">
                                {endImgStatus === "completed" ? "\u2713" : endImgStatus === "generating" ? "\u22ef" : "\u25cb"}
                              </span>
                              <span className={`tl-micro-badge tl-micro-badge-${videoStatus}`} title="Video">
                                {videoStatus === "completed" ? "\u25b6" : videoStatus === "generating" ? "\u22ef" : "\u25cb"}
                              </span>
                            </div>
                          </div>

                          {/* Playing indicator */}
                          {isPlaying && (
                            <div className="video-timeline-segment-playing-indicator" />
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          <button
            type="button"
            className="video-timeline-scroll-btn"
            onClick={() => scrollSegments(1)}
            disabled={!canScrollRight}
            aria-label="Voir les segments suivants"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Duration Display */}
      <div className="video-timeline-footer">
        <div className="video-timeline-duration">
          <span className="video-timeline-duration-label">Durée totale:</span>
          <span className="video-timeline-duration-value">{(totalDurationMs / 1000).toFixed(1)}s</span>
        </div>
        <div className="video-timeline-current-time">
          <span className="video-timeline-time-label">Position:</span>
          <span className="video-timeline-time-value">{(currentTimeMs / 1000).toFixed(2)}s</span>
        </div>
      </div>
    </div>
  );
}
